/** Term `id` values map to `/blog/glossary/[id]` and hub anchors on `/blog/glossary#id` — used by `[[def:id|label]]` in article bodies. */
export type GlossaryEntry = {
  id: string;
  title: string;
  definition: string;
  /** Optional external primer (standards body, encyclopedia, etc.) */
  learnMore?: { label: string; href: string };
};

export const GLOSSARY_ENTRIES: GlossaryEntry[] = [
  {
    id: "shrink",
    title: "Shrinkage (shrink)",
    definition:
      "Inventory recorded on the books but not available to sell — theft, damage, errors, supplier shorts, and unexplained variance. Often expressed as a percent of sales or COGS.",
    learnMore: { label: "NRF — retail loss overview", href: "https://nrf.com/resources/retails-impact" },
  },
  {
    id: "fefo",
    title: "FEFO (first expired, first out)",
    definition:
      "Rotation policy: pick or sell the soonest-expiring units first, even if newer stock arrived later. Common where shelf life or compliance dominates simple arrival order.",
  },
  {
    id: "fifo",
    title: "FIFO (first in, first out)",
    definition:
      "Rotation policy: oldest receipts leave first. Works when stock is homogeneous and dating is not critical; breaks when multiple batches with different dates sit in one lane.",
  },
  {
    id: "eoq",
    title: "Economic order quantity (EOQ)",
    definition:
      "Classic formula balancing ordering cost vs holding cost to suggest an order size. Useful as a baseline — real suppliers add MOQs, promos, and shelf-life risk.",
  },
  {
    id: "safety-stock",
    title: "Safety stock",
    definition:
      "Buffer inventory held beyond cycle stock to cover demand and lead-time variability. Should be tied to service targets and reviewed when supply stabilises.",
  },
  {
    id: "service-level",
    title: "Service level",
    definition:
      "Target probability of not stocking out during an order cycle (or fill rate). Higher service implies more inventory unless demand is smoothed or lead times shrink.",
  },
  {
    id: "bullwhip",
    title: "Bullwhip effect",
    definition:
      "Small demand changes amplify into large order swings upstream due to batching, forecast updates, rationing games, and lead-time delays.",
  },
  {
    id: "sku",
    title: "SKU (stock keeping unit)",
    definition:
      "Smallest sellable/stockable product variant tracked in inventory — size, flavour, channel-specific pack, etc. SKU count drives pick complexity and master-data cost.",
  },
  {
    id: "slob",
    title: "SLOB (slow-moving and obsolete)",
    definition:
      "Inventory with declining velocity or approaching end-of-life. Early identification reduces write-offs and frees shelf and capital.",
  },
  {
    id: "doh",
    title: "Days on hand (DOH)",
    definition:
      "Average days of sales represented by on-hand inventory — inventory intensity. Interpret alongside margin, seasonality, and expiry risk.",
  },
  {
    id: "turnover",
    title: "Inventory turnover",
    definition:
      "COGS divided by average inventory over a period — how many times inventory cycles. High turnover can still hide stock-outs; context matters.",
  },
  {
    id: "vmi",
    title: "Vendor-managed inventory (VMI)",
    definition:
      "Supplier replenishes within agreed bounds using your consumption data. Can cut stock-outs but needs trust, clean data, and clear dispute handling.",
  },
  {
    id: "moq",
    title: "MOQ (minimum order quantity)",
    definition:
      "Smallest order a supplier will accept or ship economically. MOQs often force more inventory than textbook EOQ suggests.",
  },
  {
    id: "jit",
    title: "JIT (just-in-time)",
    definition:
      "Align inbound supply closely to demand to minimise idle stock. Requires reliable partners and accurate signals — otherwise it becomes expedite chaos.",
  },
  {
    id: "jic",
    title: "JIC (just-in-case)",
    definition:
      "Hold extra stock to buffer disruption or forecast error. Rational after shocks; expensive if buffers never unwind when conditions improve.",
  },
  {
    id: "cross-dock",
    title: "Cross-docking",
    definition:
      "Move inbound goods straight to outbound with minimal storage — reduces handling cost and days in warehouse when timing aligns.",
  },
  {
    id: "quarantine",
    title: "Quarantine / hold stock",
    definition:
      "Inventory segregated from sellable stock pending QC, recall checks, or regulatory release. Must be obvious physically and in the system.",
  },
  {
    id: "perpetual-inventory",
    title: "Perpetual inventory",
    definition:
      "System inventory updated transaction-by-transaction (receipts, picks, adjustments) rather than only at physical counts.",
  },
  {
    id: "cycle-count",
    title: "Cycle counting",
    definition:
      "Counting subsets of inventory on a schedule to detect drift early — preferable to one annual wall-to-wall count for many operators.",
  },
  {
    id: "abc-analysis",
    title: "ABC analysis",
    definition:
      "Segmenting SKUs by value or margin contribution — A items are few but material; policies (counts, safety stock) differ by class.",
  },
  {
    id: "xyz-analysis",
    title: "XYZ analysis",
    definition:
      "Segmenting SKUs by demand variability — X stable, Z erratic. Combined with ABC to prioritise effort and buffers.",
  },
  {
    id: "3pl",
    title: "3PL (third-party logistics)",
    definition:
      "Outsourced warehousing and transport. You still own inventory risk — SLAs and data feeds matter as much as price.",
  },
  {
    id: "working-capital",
    title: "Working capital",
    definition:
      "Operating liquidity tied up in receivables, payables, and inventory. Inventory reduction frees cash if service and compliance are preserved.",
  },
  {
    id: "write-off",
    title: "Write-off",
    definition:
      "Recognising inventory as unsellable or obsolete and removing its value from books — should trace to approvals and physical evidence.",
  },
  {
    id: "stockout",
    title: "Stock-out",
    definition:
      "No available inventory to meet demand at a location or channel — lost sales, substitution, or customer churn depending on category.",
  },
  {
    id: "obsolescence",
    title: "Obsolescence",
    definition:
      "Inventory loses value because demand moved, specs changed, or regulation shifted — distinct from slow seasonal demand.",
  },
  {
    id: "traceability",
    title: "Lot / batch traceability",
    definition:
      "Ability to trace a unit forward to customers and backward to supplier batches — critical for recalls and quality investigations.",
  },
  {
    id: "consignment",
    title: "Consignment inventory",
    definition:
      "Stock physically at your site but owned by the supplier until sale or use — improves your cash timing but needs clear contracts and system flags.",
  },
];

export const glossaryById: Record<string, GlossaryEntry> = Object.fromEntries(GLOSSARY_ENTRIES.map((e) => [e.id, e]));
